import React from 'react';

import { Box } from '@rbilabs/universal-components';

import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { LoyaltyOffersCooldownDrawer } from 'pages/loyalty/loyalty-offers/loyalty-offers-cooldown-drawer';
import { selectors, useAppSelector } from 'state/global-state';
import { Route, Routes } from 'state/location';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';

import { BrowsingPanel } from './browsing-panel';
import { DetailsPanel } from './details-panel';
import { DetailsPanelError } from './details-panel-error';
import { MobileBackButton } from './mobile-back-button';
import { useOffersUIContext } from './offers-ui-context';

export const offerIdSlug = ':offerId';

const BrowsingPanelContainer = Box.withConfig({
  flexGrow: 1,
  flexShrink: 1,
  flexBasis: 'auto',
  backgroundColor: Styles.color.background,
  minWidth: {
    md: '450px',
  },
  maxWidth: {
    md: '550px',
  },
});

const DetailsPanelContainer = Box.withConfig({
  flexGrow: {
    base: 1,
    md: 2,
  },
  flexShrink: {
    base: 1,
  },
  flexBasis: {
    base: 'auto',
  },
});

export const BrowsingRoute = () => {
  return (
    <BrowsingPanelContainer>
      <BrowsingPanel />
    </BrowsingPanelContainer>
  );
};

export const DetailsRoute = () => {
  const isMobile = useIsMobileBreakpoint();
  const {
    previewOfferId,
    previewOffer,
    showEmptyState,
    showMissingOffer,
    featureOffers,
  } = useOffersUIContext();
  const loyaltyOffersInCooldown = useAppSelector(selectors.loyalty.selectLoyaltyOffersInCooldown);
  const { offersDetailsPanel = null, defaultBackgroundImage = null } = featureOffers || {};
  if (showEmptyState) {
    return (
      <DetailsPanelError
        backgroundImage={defaultBackgroundImage}
        image={offersDetailsPanel?.emptyStateImage ?? null}
        title={offersDetailsPanel?.emptyStateTitle ?? null}
        body={offersDetailsPanel?.emptyStateBody ?? null}
        action={offersDetailsPanel?.emptyStateAction ?? null}
        showAction
        titleTestId="details-panel-empty-state-title"
        bodyTestId="details-panel-empty-state-body"
        actionButtonTestId="details-panel-empty-state-action-button"
      />
    );
  }

  if (showMissingOffer) {
    return (
      <DetailsPanelError
        backgroundImage={defaultBackgroundImage}
        image={offersDetailsPanel?.missingOfferImage ?? null}
        title={offersDetailsPanel?.missingOfferTitle ?? null}
        body={offersDetailsPanel?.missingOfferBody ?? null}
        action={offersDetailsPanel?.missingOfferAction ?? null}
        showAction={isMobile}
        titleTestId="details-panel-missing-offer-title"
        bodyTestId="details-panel-missing-offer-body"
        actionButtonTestId="details-panel-missing-offer-action-button"
      />
    );
  }

  return (
    <DetailsPanelContainer>
      {loyaltyOffersInCooldown && <LoyaltyOffersCooldownDrawer />}
      {previewOffer && (
        <DetailsPanel
          key={previewOfferId ?? ''}
          offer={previewOffer}
          // props for DetailsPanelError
          backgroundImage={defaultBackgroundImage}
          image={offersDetailsPanel?.missingOfferImage ?? null}
          title={offersDetailsPanel?.missingOfferTitle ?? null}
          body={offersDetailsPanel?.missingOfferBody ?? null}
          action={offersDetailsPanel?.missingOfferAction ?? null}
          showAction={isMobile}
          offersInCooldown={loyaltyOffersInCooldown}
        />
      )}
    </DetailsPanelContainer>
  );
};

export const OffersRouter: React.FC<React.PropsWithChildren<unknown>> = () => {
  const isMobile = useIsMobileBreakpoint();
  const { showMobileBackButton, offersUrl, offers, loading } = useOffersUIContext();
  const { logEvent } = useMParticleContext();
  const length = offers.length;

  useEffectOnUpdates(() => {
    if (loading) {
      return;
    }

    logEvent(CustomEventNames.OFFERS_DISPLAYED, EventTypes.Other, { count: length });
  }, [loading, length, logEvent]);

  return isMobile ? (
    <>
      {showMobileBackButton && <MobileBackButton to={offersUrl} />}
      <Routes>
        <Route path={''} element={<BrowsingRoute />} />
        <Route path={'/*'} element={<DetailsRoute />} />
      </Routes>
    </>
  ) : (
    <>
      <Route path={''} element={<BrowsingRoute />} />
      <Routes>
        <Route path={'/*'} element={<DetailsRoute />} />
      </Routes>
    </>
  );
};
